import { useState } from "react";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { Button, Empty, Input, Tooltip, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import type { Controller } from "../../../services/licensing/client/types.gen";
import type { SiteIoConfiguration } from "../../../services/client/types.gen";
import { isVersion, stripVersionPrefix } from "../../../util/controllerVersion";
import { ControllerCard } from "./ControllerCard";
import { controllerStatusOf } from "./controllerStatus";
import type { BoardColumn } from "./boardColumns";

const { Text } = Typography;

export type ControllerDragData = { controllerId: string };
export type VersionDropData = { version?: string };

type ColumnsProps = {
  columns: BoardColumn[];
  boardKey: string;
  partnerNameById: Record<string, string>;
  ioConfigurationBySiteId: Record<string, SiteIoConfiguration>;
  draggable?: boolean;
  droppable?: boolean;
  pendingControllerId?: string;
  onAddVersion?: (version: string) => void;
  onOpenSettings: (controller: Controller) => void;
};

const openSettingsHandler = (
  controller: Controller,
  onOpenSettings: (controller: Controller) => void,
) =>
  controller.site_id && controller.partner_id
    ? () => onOpenSettings(controller)
    : undefined;

const DraggableControllerCard: React.FC<{
  controller: Controller;
  boardKey: string;
  partnerName?: string;
  siteIoConfiguration?: SiteIoConfiguration;
  pending?: boolean;
  onOpenSettings?: () => void;
}> = ({
  controller,
  boardKey,
  partnerName,
  siteIoConfiguration,
  pending,
  onOpenSettings,
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `${boardKey}-${controller._id}`,
    data: { controllerId: controller._id ?? "" } satisfies ControllerDragData,
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`cursor-grab ${isDragging || pending ? "opacity-40" : ""}`}
      aria-roledescription="draggable controller"
      aria-label={`Drag ${controller.device_info?.serial_numbers?.hostname ?? controller._id} onto a version to target it`}
    >
      <ControllerCard
        controller={controller}
        status={controllerStatusOf(controller)}
        partnerName={partnerName}
        siteIoConfiguration={siteIoConfiguration}
        onOpenSettings={onOpenSettings}
      />
    </div>
  );
};

const Column: React.FC<
  Omit<ColumnsProps, "columns"> & { column: BoardColumn }
> = ({
  column,
  boardKey,
  partnerNameById,
  ioConfigurationBySiteId,
  draggable,
  droppable,
  pendingControllerId,
  onOpenSettings,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `${boardKey}-${column.key}`,
    disabled: !droppable || !column.version,
    data: { version: column.version } satisfies VersionDropData,
  });

  return (
    <div
      ref={setNodeRef}
      className={`flex min-h-[10rem] flex-col rounded-lg border border-dashed p-3 transition-colors ${
        isOver
          ? "border-primary-500 bg-primary-500/[0.06]"
          : "border-transparent bg-gray-100"
      }`}
    >
      <Tooltip title={column.hint}>
        <div className="mb-3 flex items-baseline justify-between gap-2">
          <Text strong className="text-base break-all">
            {column.title}
          </Text>
          <Text type="secondary" className="tabular-nums">
            {column.controllers.length}
          </Text>
        </div>
      </Tooltip>

      {column.controllers.length === 0 ? (
        <div className="flex flex-1 items-center justify-center">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="None"
            className="!my-0"
          />
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {column.controllers.map((controller) => {
            const partnerName = controller.partner_id
              ? partnerNameById[controller.partner_id]
              : undefined;
            const siteIoConfiguration = controller.site_id
              ? ioConfigurationBySiteId[controller.site_id]
              : undefined;
            return draggable ? (
              <DraggableControllerCard
                key={controller._id}
                controller={controller}
                boardKey={boardKey}
                partnerName={partnerName}
                siteIoConfiguration={siteIoConfiguration}
                pending={
                  !!controller._id && controller._id === pendingControllerId
                }
                onOpenSettings={openSettingsHandler(controller, onOpenSettings)}
              />
            ) : (
              <ControllerCard
                key={controller._id}
                controller={controller}
                status={controllerStatusOf(controller)}
                partnerName={partnerName}
                siteIoConfiguration={siteIoConfiguration}
                onOpenSettings={openSettingsHandler(controller, onOpenSettings)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

const AddVersionColumn: React.FC<{ onAdd: (version: string) => void }> = ({
  onAdd,
}) => {
  const [draft, setDraft] = useState<string | null>(null);
  const version = stripVersionPrefix(draft ?? "");

  if (draft === null) {
    return (
      <button
        type="button"
        onClick={() => setDraft("")}
        className="hover:border-primary-500 hover:text-primary-500 flex min-h-[10rem] flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-gray-300 p-3 text-gray-500 transition-colors"
      >
        <PlusOutlined />
        <span>Add version</span>
      </button>
    );
  }

  const submit = () => {
    if (isVersion(version)) {
      onAdd(version);
      setDraft(null);
    }
  };

  return (
    <div className="flex min-h-[10rem] flex-col gap-2 rounded-lg border border-dashed border-gray-300 p-3">
      <Input
        autoFocus
        value={draft}
        placeholder="1.2.3"
        status={version && !isVersion(version) ? "error" : undefined}
        onChange={(event) => setDraft(event.target.value)}
        onPressEnter={submit}
        onKeyDown={(event) => event.key === "Escape" && setDraft(null)}
      />
      <Text type="secondary" className="text-xs">
        Adds an empty column to drag controllers onto, even if nothing runs this
        version yet.
      </Text>
      <div className="mt-auto flex gap-2">
        <Button type="primary" disabled={!isVersion(version)} onClick={submit}>
          Add
        </Button>
        <Button type="text" onClick={() => setDraft(null)}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

export const ControllerColumns: React.FC<ColumnsProps> = ({
  columns,
  onAddVersion,
  ...columnProps
}) => (
  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
    {columns.map((column) => (
      <Column key={column.key} column={column} {...columnProps} />
    ))}
    {onAddVersion && <AddVersionColumn onAdd={onAddVersion} />}
  </div>
);
